import * as THREE from 'three';
import { SHOW_CLUSTER_HAZE } from '../../constants';
import { LAYOUT_JUMP_DIST2 } from '../../constants';
import { getGraphStructureKey, markIslandJump } from '../../utils/neuralSceneUtils';
import { syncClusterNebula } from '../neuralMaterials';
import { edgeStyle } from './edges';

export function createGraphSync({
  graphRef,
  meshByTaskId,
  labelByTaskId,
  edgeLinesByKey,
  nebula,
  selectionRing,
  createMeshesForNode,
  reconcileNodePresentation,
  reconcileEdges,
  reconcileHulls,
  rebuildRaycastTargets,
}) {
  /** Last graph topology key synced to meshes (null on first sync) */
  let appliedStructureKey = null;
  let selectedTaskIdLocal = null;
  const _jumpTo = new THREE.Vector3();

  function writeNodeTarget(mesh, pos) {
    const tgt = mesh.userData.targetPos;
    if (!tgt) {
      mesh.userData.targetPos = new THREE.Vector3(...pos);
      if (!mesh.userData.homePos) mesh.userData.homePos = mesh.userData.targetPos.clone();
      return;
    }
    _jumpTo.set(pos[0], pos[1], pos[2]);
    markIslandJump(mesh.userData, mesh.userData.homePos || tgt, _jumpTo, LAYOUT_JUMP_DIST2);
    tgt.copy(_jumpTo);
  }

  function syncNebula(g) {
    nebula.visible = SHOW_CLUSTER_HAZE && (g.nodes?.length > 0);
    if (nebula.visible) syncClusterNebula(nebula, g.clusters);
  }

  function ensureGraphSynced() {
    const g = graphRef.current;
    const key = getGraphStructureKey(g);
    if (key === appliedStructureKey) return;
    appliedStructureKey = key;

    const { nodes } = g;
    syncNebula(g);
    const incomingIds = new Set(nodes.map((n) => n.id));

    meshByTaskId.forEach((mesh, tid) => {
      if (!incomingIds.has(tid)) {
        mesh.userData.exiting = true;
      } else {
        mesh.userData.exiting = false;
        mesh.userData.visT = Math.max(mesh.userData.visT, mesh.userData.baseOpacity * 0.82);
      }
    });

    nodes.forEach((node) => {
      let mesh = meshByTaskId.get(node.id);
      if (!mesh) {
        mesh = createMeshesForNode(node);
        meshByTaskId.set(node.id, mesh);
        reconcileNodePresentation(mesh, node);
      } else {
        reconcileNodePresentation(mesh, node);
      }
      writeNodeTarget(mesh, node.pos);
    });

    reconcileEdges(g);
    reconcileHulls(g);
    rebuildRaycastTargets();
  }

  const syncSelection = (taskId) => {
    selectedTaskIdLocal = taskId ?? null;
    meshByTaskId.forEach((m) => {
      if (m.userData.exiting) return;
      const ud = m.userData;
      m.material.color.copy(ud.baseColor);
    });
    labelByTaskId.forEach(({ element: el }, tid) => {
      el.classList.toggle('neural-view__node-tag--selected', tid === taskId);
    });
    const subj = meshByTaskId.get(taskId);
    if (taskId && subj && !subj.userData.exiting && subj.visible) {
      selectionRing.visible = true;
      selectionRing.position.copy(subj.position);
    } else {
      selectionRing.visible = false;
    }
  };

  const refreshPresentation = () => {
    const prevKey = appliedStructureKey;
    ensureGraphSynced();
    if (prevKey && prevKey !== appliedStructureKey) {
      window.dispatchEvent(new CustomEvent('closeTableDropdowns'));
    }
    const g = graphRef.current;
    syncNebula(g);
    const gn = g.nodes;
    gn.forEach((node) => {
      const m = meshByTaskId.get(node.id);
      if (!m || m.userData.exiting) return;
      reconcileNodePresentation(m, node);
      writeNodeTarget(m, node.pos);
    });

    const nodeById = new Map(gn.map((node) => [node.id, node]));
    edgeLinesByKey.forEach((lineObj) => {
      const ud = lineObj.userData;
      if (!ud.exiting && ud.sourceNodeMeta) {
        ud.sourceNodeMeta = nodeById.get(ud.fromId) || ud.sourceNodeMeta;
        ud.targetNodeMeta = nodeById.get(ud.toId) || ud.targetNodeMeta;
        ud.targetOpacity = edgeStyle(ud.kind, ud.sourceNodeMeta, ud.targetNodeMeta).opacity;
        ud.styleDirty = true;
      }
    });

    syncSelection(selectedTaskIdLocal);
  };

  return {
    ensureGraphSynced,
    refreshPresentation,
    syncSelection,
    getSelectedTaskId: () => selectedTaskIdLocal,
  };
}
